// Palworld TCG Card Marketplace — a TCGplayer-style shopping grid over the same
// validated price board that powers the price chart and the price index, so a
// card can never show one value here and another there. Every tile links to the
// card's own page, its price-chart row, the index and the matching guide.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPriceBoard } from "@/lib/prices.functions";
import {
  CURRENCIES,
  PRICE_SOURCES,
  TRACKED_CARD_COUNT,
  buildPriceRows,
  formatMoney,
  type CurrencyCode,
  type PriceRow,
} from "@/lib/prices-shared";
import { OFFICIAL_CARDS, OFFICIAL_CARD_BY_CODE, SET_NAMES } from "@/lib/tcg-cards";
import { CardImage } from "@/components/CardImage";
import { RatingStars } from "@/components/CardRating";
import { RarityBadge } from "@/components/RarityBadge";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { ShareButton } from "@/components/ShareButton";
import { breadcrumbSchema, toolSchema, cardTableListSchema, collectionSchema } from "@/lib/schema";

// Cards shown on the marketplace board; prices load live per visit, so the list
// points at each card page for its Product/AggregateOffer markup.
const TRACKED_LIST = OFFICIAL_CARDS.filter((c) => c.image).map((c) => ({ code: c.code, name: c.name }));

import { trackAffiliateClick } from "@/lib/affiliate-clicks";
import { goUrl } from "@/lib/outbound";
import { cardLinkProps } from "@/lib/card-link";

const URL = "https://palstars.com/card-marketplace";
const TITLE = "Palworld TCG Card Marketplace — Buy Singles";
const DESCRIPTION =
  "Palworld TCG Card Marketplace: browse every printed Palworld card with its live market price, lowest validated listing and daily trend, then open the cheapest listing.";

type SortKey = "market" | "code" | "name";

const FAQ: FaqItem[] = [
  {
    q: "What is the Palworld TCG Card Marketplace?",
    a: "It is a card-by-card buying board for Palworld OFFICIAL CARD GAME singles. Every tile shows the validated market price, the Pal Stars Index baseline, and direct links to buy the card, to its price-chart history and to the guide that covers it.",
  },
  {
    q: "How is the marketplace price for each Palworld card set?",
    a: "It is the same blended market value used on the Palworld TCG price chart: the median of every validated marketplace listing and completed sale for that printed card number, with lots, proxies, sealed product and graded slabs filtered out and outliers rejected. Prices refresh automatically every day.",
  },
  {
    q: "Can I buy Palworld cards directly on Pal Stars?",
    a: "No. Pal Stars is an unofficial fan database, not a store. The marketplace links out to the marketplaces that actually list Palworld singles, and Pal Stars may earn an affiliate commission on a purchase at no extra cost to you.",
  },
  {
    q: "Which Palworld cards are the best value right now?",
    a: "Compare each card's market price with its Pal Stars Index baseline and check liquidity on the card's price-chart row. A cheap-looking card with almost no listings or sales is a thin market, not a bargain.",
  },
];

export const Route = createFileRoute("/card-marketplace")({
  // Server-side render from the real price board so first paint (crawler and
  // browser alike) matches the hydrated client render.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["price-board"],
      queryFn: () => getPriceBoard(),
      staleTime: 5 * 60 * 1000,
    }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld TCG Card Marketplace — buy singles by value" },
      { property: "og:description", content: "Every Palworld card with its live market price, lowest listing and daily trend." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld TCG", "/palworld-tcg"], ["Card Marketplace", "/card-marketplace"]]),
      toolSchema({ name: "Palworld TCG Card Marketplace", description: DESCRIPTION, path: "/card-marketplace" }),
      faqSchema(FAQ, URL),
      collectionSchema({
        name: "Palworld TCG Card Marketplace",
        description: `Directory of all ${TRACKED_CARD_COUNT} Palworld OFFICIAL CARD GAME singles listed on the Pal Stars marketplace board.`,
        path: "/card-marketplace",
        items: TRACKED_LIST.slice(0, 100).map((c) => ({ name: `${c.code} ${c.name}`, path: `/cards/${c.code}` })),
        numberOfItems: TRACKED_CARD_COUNT,
      }),
      cardTableListSchema({
        name: "Palworld TCG cards on the card marketplace",
        description: "Palworld OFFICIAL CARD GAME singles tracked on the Pal Stars card marketplace board.",
        path: "/card-marketplace",
        cards: TRACKED_LIST,
      }),
    ],
  }),
  component: CardMarketplacePage,
});

function MarketTile({ row, currency }: { row: PriceRow; currency: CurrencyCode }) {
  const card = OFFICIAL_CARD_BY_CODE.get(row.code.toUpperCase());
  const buy = PRICE_SOURCES.find((s) => s.id === "ebay");
  const tcg = PRICE_SOURCES.find((s) => s.id === "tcgplayer");
  return (
    <li className="glass flex flex-col rounded-2xl p-3">
      <Link {...cardLinkProps(row.code)} className="group">
        <CardImage
          src={row.image}
          alt={`${row.name} ${row.code} — official Palworld TCG card`}
          width={220}
          height={306}
          responsive
          className="w-full rounded-lg"
        />
        <p className="mt-2 truncate text-sm font-bold group-hover:text-primary">{row.name}</p>
      </Link>
      <RatingStars code={row.code} label={`${row.code} ${row.name}`} className="mt-1" />
      <p className="text-[11px] text-muted-foreground">
        {row.code} · {SET_NAMES[row.set] ?? row.set}
      </p>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className="text-lg font-extrabold tabular-nums">{formatMoney(row.market, currency)}</span>
        <RarityBadge code={row.rarity} />
      </div>
      <dl className="mt-1.5 space-y-0.5 text-[11px]">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Index baseline</dt>
          <dd className="tabular-nums">{formatMoney(row.index, currency)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Lowest listing</dt>
          <dd className="tabular-nums">{formatMoney(row.low, currency)}</dd>
        </div>
      </dl>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
        {card && buy ? (
          <a
            href={goUrl(buy.id, "card", row.code)}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={() => trackAffiliateClick({ source: buy.id, kind: "card", targetKey: row.code })}
            className="btn-ebay"
          >
            Buy On Ebay
          </a>
        ) : null}
        {card && tcg ? (
          <a
            href={goUrl(tcg.id, "card", row.code)}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={() => trackAffiliateClick({ source: tcg.id, kind: "card", targetKey: row.code })}
            className="btn-tcgplayer"
          >
            Buy TCGPlayer
          </a>
        ) : null}
        {/* Extra purchase option only: Asian-printing listings never feed the
            English market value, price chart or index. */}
        {card ? (
          <a
            href={goUrl("ebay-jp", "card", row.code)}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={() => trackAffiliateClick({ source: "ebay-jp", kind: "card", targetKey: row.code })}
            title="Japanese, Chinese and Korean printings on eBay — auctions and Buy It Now"
            className="btn-ebay"
          >
            Buy JP/CN/KR
          </a>
        ) : null}

        {/* Price history and the guide both open THIS card's own page, so the
            link never dumps the visitor on the full chart with no card selected. */}
        <Link {...cardLinkProps(row.code)} className="rounded-md border border-border/60 px-2 py-[3px] font-semibold hover:bg-card">
          Price history
        </Link>
        <Link
          {...cardLinkProps(row.code)}
          hash="card-guide"
          className="rounded-md border border-border/60 px-2 py-[3px] font-semibold hover:bg-card"
        >
          Guide &amp; FAQ
        </Link>
      </div>
    </li>
  );
}

function CardMarketplacePage() {
  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    refetchOnMount: "always",
    refetchInterval: 15 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    // No render-time stamp: the disclosure must show the real last sync only.
  });

  const rows = data?.rows ?? [];
  const [query, setQuery] = useState("");
  const [set, setSet] = useState("all");
  const [sort, setSort] = useState<SortKey>("market");
  const [currency, setCurrency] = useState<CurrencyCode>("USD");
  const [limit, setLimit] = useState(48);

  const sets = useMemo(() => [...new Set(rows.map((r) => r.set))], [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = rows.filter((r) => {
      if (set !== "all" && r.set !== set) return false;
      if (!q) return true;
      return r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q) || r.subtitle.toLowerCase().includes(q);
    });
    return [...out].sort((a, b) => {
      switch (sort) {
        case "code":
          return a.code.localeCompare(b.code);
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return b.market - a.market;
      }
    });
  }, [rows, query, set, sort]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <header className="mt-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <h1 className="text-3xl font-extrabold sm:text-4xl">Palworld TCG Card Marketplace</h1>
          <ShareButton
            title="Palworld TCG Card Marketplace — Pal Stars"
            text={`Browse ${TRACKED_CARD_COUNT} Palworld OFFICIAL CARD GAME singles with live prices and direct listing links.`}
            path="/card-marketplace"
          />
        </div>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          Browse all {TRACKED_CARD_COUNT} printed Palworld OFFICIAL CARD GAME singles as a shopping board. Every card
          carries its validated market price, its lowest validated listing, the transparent Pal Stars Index baseline and a
          direct link to the live listing. Prices are the same validated feed used by the Palworld TCG price chart and price index, refreshed
          automatically every day.
        </p>
      </header>

      <AffiliateDisclosure updated={data?.updated} className="mt-4" />

      <div className="glass sticky top-16 z-20 mt-6 flex flex-wrap items-center gap-2 rounded-2xl p-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search card name or number…"
          aria-label="Search Palworld cards"
          className="min-w-48 flex-1 rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm"
        />
        <select aria-label="Set" value={set} onChange={(e) => setSet(e.target.value)} className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm">
          <option value="all">All sets</option>
          {sets.map((s) => (
            <option key={s} value={s}>
              {SET_NAMES[s] ?? s}
            </option>
          ))}
        </select>
        <select aria-label="Sort by" value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm">
          <option value="market">Highest value</option>
          <option value="code">Card number</option>
          <option value="name">Name A–Z</option>
        </select>
        <select aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value as CurrencyCode)} className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm">
          {CURRENCIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code}
            </option>
          ))}
        </select>
      </div>

      <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {filtered.slice(0, limit).map((r) => (
          <MarketTile key={r.code} row={r} currency={currency} />
        ))}
      </ul>

      {filtered.length > limit ? (
        <button
          type="button"
          onClick={() => setLimit((l) => l + 48)}
          className="mx-auto mt-6 block rounded-lg border border-border/60 px-5 py-2.5 text-sm font-semibold hover:bg-card"
        >
          Show more cards ({filtered.length - limit} left)
        </button>
      ) : null}

      <section className="glass mt-10 rounded-2xl p-5">
        <h2 className="text-xl font-bold">Where to go next</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li>
            <Link to="/prices" className="font-semibold text-primary hover:underline">
              Palworld TCG price chart →
            </Link>{" "}
            full 30-day history, sold data and graded values per card.
          </li>
          <li>
            <Link to="/tcg-price-index" className="font-semibold text-primary hover:underline">
              Palworld TCG price index →
            </Link>{" "}
            is the market as a whole rising or falling?
          </li>
          <li>
            <Link to="/marketplace" className="font-semibold text-primary hover:underline">
              Sealed &amp; merch marketplace →
            </Link>{" "}
            packs, booster boxes, Trial Decks and official merch.
          </li>
          <li>
            <Link to="/collection" className="font-semibold text-primary hover:underline">
              Collection tracker →
            </Link>{" "}
            log what you paid and track profit per card.
          </li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld TCG Card Marketplace FAQ" />
    </div>
  );
}
